// Extracted from Dashboard/index.web.jsx on 2026-10-02.
import dashboardConfig from '../config/dashboard.config';
import type {
  CreateLayoutsOptions,
  DashboardBreakpoint,
  DashboardLayouts,
  GridItem,
  GridPosition,
} from '../types/dashboard.types';
import { buildGridItemLayout } from './widgetSizing.utils';

const collidesLayoutItems = (a: GridPosition, b: GridPosition) =>
  a.x < b.x + b.w && a.x + a.w > b.x && a.y < b.y + b.h && a.y + a.h > b.y;
export const findNextGridPlacement = (items: GridPosition[], cols: number, w = 1, h = 1) => {
  const normalizedWidth = Math.max(1, Math.min(cols, Number(w) || 1));
  const normalizedHeight = Math.max(1, Number(h) || 1);
  const maxRow = Math.max(0, ...items.map((item) => (Number(item.y) || 0) + (Number(item.h) || 1)));

  for (let y = 0; y <= maxRow + 100; y++) {
    for (let x = 0; x <= cols - normalizedWidth; x++) {
      const candidate = { x, y, w: normalizedWidth, h: normalizedHeight };
      if (items.every((existingItem) => !collidesLayoutItems(existingItem, candidate))) {
        return { x, y };
      }
    }
  }

  return { x: 0, y: maxRow + 1 };
};

const repackLayoutItemsForEdgeCase = (items: GridItem[], cols: number) => {
  if (!Array.isArray(items) || cols <= 1) {
    return items;
  }

  const hasTallWidgets = items.some((item) => Number(item?.w) === 1 && Number(item?.h) === 2);
  const hasWideSingleRowWidgets = items.some(
    (item) => Number(item?.h) === 1 && Number(item?.w) > 1,
  );
  if (!hasTallWidgets || hasWideSingleRowWidgets) {
    return items;
  }

  const orderedItems = [...items].sort((a, b) => {
    const yDiff = (Number(a?.y) || 0) - (Number(b?.y) || 0);
    if (yDiff !== 0) return yDiff;

    const xDiff = (Number(a?.x) || 0) - (Number(b?.x) || 0);
    if (xDiff !== 0) return xDiff;

    return String(a?.i ?? '').localeCompare(String(b?.i ?? ''), undefined, { numeric: true });
  });

  const placedItems: GridItem[] = [];

  return orderedItems.map((item) => {
    const normalizedWidth = Math.max(1, Math.min(cols, Number(item?.w) || 1));
    const normalizedHeight = Math.max(1, Number(item?.h) || 1);
    const { x, y } = findNextGridPlacement(placedItems, cols, normalizedWidth, normalizedHeight);

    const packedItem = {
      ...item,
      x,
      y,
      w: normalizedWidth,
      h: normalizedHeight,
    };

    placedItems.push(packedItem);
    return packedItem;
  });
};

export const createDashboardLayouts = ({
  list,
  definedWidgetData,
  canEditWidgets,
}: CreateLayoutsOptions): DashboardLayouts => {
  const layouts = {} as DashboardLayouts;
  for (const breakpoint of Object.keys(
    dashboardConfig['columns-by-breakpoint'],
  ) as DashboardBreakpoint[]) {
    const cols = dashboardConfig['columns-by-breakpoint'][breakpoint];
    const items = list.map((item, index) =>
      buildGridItemLayout({
        item,
        index,
        cols,
        forceSingleColumnWidth: cols === 1,
        definedWidgetData,
        canEditWidgets,
      }),
    );
    layouts[breakpoint] = [
      ...repackLayoutItemsForEdgeCase(items, cols),
      {
        i: 'add',
        displayed: true,
        isDraggable: false,
        isResizable: false,
        locked: true,
        static: false,
        x: 0,
        y: Infinity,
        w: 1,
        h: 1,
      },
    ];
  }
  return layouts;
};
